import * as Yup from "yup";
import { validationMessages } from "../common/validationMessages";
import { PickupStatus, StopStatus } from "../common/route";
import { AddressModel, AddressWithCoordinates } from "../common/address";
import { ItemModel } from "../common/models";
import { Coordinates } from "../map/models";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";

export const TIME_PERIOD_LIST: {
  [key: string]: string;
} = {
  THIS_WEEK: "This Week",
  LAST_WEEK: "Last Week",
  THIS_MONTH: "This Month",
  LAST_MONTH: "Last Month",
  THIS_QUARTER: "This Quarter",
  LAST_QUARTER: "Last Quarter",
  CUSTOM: "Custom"
};

export const PICKUP_REASON_OPTIONS: XGSSelectOption[] = [
  { value: "Customer not ready", label: "Customer not ready" },
  { value: "Incomplete pickup information", label: "Incomplete pickup information" },
  { value: "No driver in the area", label: "No driver in the area" },
  { value: "Missed pickup window", label: "Missed pickup window" }
];

export interface PickupShipperContacts {
  firstName?: string;
  lastName?: string;
  mobilePhone?: string;
  allowSms?: boolean;
  email?: string;
};

export interface PickupShipperDetailsModel extends PickupShipperContacts {
  accountNumber: number;
  name: string;
  address: AddressModel;
}

export interface PickupShipperModel {
  teamId: string;
  teamName?: string;
  bolNumber?: string;
  poNumber?: string;
  additionalReference?: string;
  specialInstructions: string | undefined;
  pickupDate: string;
  pickupReadyTime: string | undefined;
  pickupCloseTime: string | undefined;
  shipper: {
    id?: string | null;
    accountNumber: number | null;
    name: string;
    address: AddressModel,
    contacts: PickupShipperContacts;
  }
};

export const PickupShipperSchema: Yup.ObjectSchema<PickupShipperModel> = Yup.object<PickupShipperModel>({
  teamId: Yup.string().required(validationMessages.required),
  bolNumber: Yup.string().matches(/(\w+|-+)/, validationMessages.notValid).max(20, "The value must be no more than 20 characters"),
  poNumber: Yup.string().matches(/(\w+|-+)/, validationMessages.notValid).max(12, "The value must be no more than 12 characters"),
  additionalReference: Yup.string().matches(/(\w+|-+)/, validationMessages.notValid).max(25, "The value must be no more than 25 characters"),
  specialInstructions: Yup.string().matches(/(\w+|-+)/, validationMessages.notValid).max(150, "The value must be no more than 150 characters"),
  pickupDate: Yup.string().required(validationMessages.required),
  pickupReadyTime: Yup.string().required(validationMessages.required),
  pickupCloseTime: Yup.string().required(validationMessages.required),
  shipper: Yup.object().shape({
    accountNumber: Yup.number().nullable().required(validationMessages.required),
    name: Yup.string().required(validationMessages.required).max(36, "The value must be no more than 36 characters"),
    address: Yup.object().shape({
      address1: Yup.string().required(validationMessages.required).max(256, "The value must be no more than 255 characters"),
      city: Yup.string().required(validationMessages.required).max(64, "The value must be no more than 64 characters"),
      postalCode: Yup.string().matches(/^\d*$/, validationMessages.notValid).min(5, "The value must be no less than 5 digits").max(5, "The value must be no more than 5 digits").required(validationMessages.required),
      state: Yup.string().required(validationMessages.required),
      additionalAddressLine: Yup.string().matches(/(\w+|-+)/, validationMessages.notValid).max(150, "The value must be no more than 150 characters"),
    }).defined(),
    contacts: Yup.object().shape({
      firstName: Yup.string().matches(/(\w+|-+)/, validationMessages.notValid).max(30, "The value must be no more than 30 characters"),
      lastName: Yup.string().matches(/(\w+|-+)/, validationMessages.notValid).max(30, "The value must be no more than 30 characters"),
      mobilePhone: Yup.string()
        .matches(/^\(\d{3}\)\s\d{3}-\d{4}$/, validationMessages.notValid)
        .max(25, "The value must be no more than 25 characters"),
      allowSms: Yup.boolean(),
      email: Yup.string().max(60, "The value must be no more than 60 characters").email(validationMessages.notValid).required(validationMessages.required)
    }).defined()
  }).defined()
}).defined();

export interface PickupConsigneeModel {
  name: string;
  address: AddressModel,
  phone: string;
  email?: string;
};

export const PickupConsigneeSchema: Yup.ObjectSchema<PickupConsigneeModel> = Yup.object<PickupConsigneeModel>({
  name: Yup.string().required(validationMessages.required).max(36, "The value must be no more than 36 characters"),
  address: Yup.object().shape({
    address1: Yup.string().required(validationMessages.required).max(256, "The value must be no more than 255 characters"),
    city: Yup.string().required(validationMessages.required).max(64, "The value must be no more than 64 characters"),
    postalCode: Yup.string().matches(/^\d*$/, validationMessages.notValid).min(5, "The value must be no less than 5 digits").max(5, "The value must be no more than 5 digits").required(validationMessages.required),
    state: Yup.string().required(validationMessages.required),
    additionalAddressLine: Yup.string().matches(/(\w+|-+)/, validationMessages.notValid).max(150, "The value must be no more than 150 characters"),
  }).defined(),
  phone: Yup.string().matches(/^\(\d{3}\)\s\d{3}-\d{4}$/, validationMessages.notValid).required(validationMessages.required),
  email: Yup.string().max(60, "The value must be no more than 60 characters").email(validationMessages.notValid)
}).defined();

export const PickupItemSchema: Yup.ObjectSchema<ItemModel> = Yup.object({
  packageType: Yup.string().required(validationMessages.required),
  units: Yup
    .number()
    .integer("The value must be a valid amount without decimal values")
    .positive(validationMessages.positive)
    .max(9999, validationMessages.max(9999))
    .typeError(validationMessages.notValid)
    .required(validationMessages.required),
  yards: Yup
    .number()
    .positive(validationMessages.positive)
    .max(99999, validationMessages.max(99999))
    .typeError(validationMessages.notValid)
    .when("packageType", {
      is: "ROLL",
      then: Yup
        .number()
        .moreThan(0, validationMessages.atLeastOne)
        .positive(validationMessages.positive)
        .max(99999, validationMessages.max(99999))
        .typeError(validationMessages.notValid)
        .required(validationMessages.required)
    })
    .when("ratingClass", {
      is: "1",
      then: Yup
        .number()
        .positive(validationMessages.positive)
        .max(99999, validationMessages.max(99999))
        .typeError(validationMessages.notValid)
        .required(validationMessages.required)
    })
    .when("ratingClass", {
      is: "3",
      then: Yup
        .number()
        .positive(validationMessages.positive)
        .max(99999, validationMessages.max(99999))
        .typeError(validationMessages.notValid)
        .required(validationMessages.required)
    }),
  weight: Yup
    .number()
    .integer("The value must be a weight without decimal values")
    .positive(validationMessages.positive)
    .max(99999, validationMessages.max(99999))
    .typeError(validationMessages.notValid)
    .required(validationMessages.required),
  length: Yup
    .number()
    .positive(validationMessages.positive)
    .max(99999, validationMessages.max(99999))
    .typeError(validationMessages.notValid),
  width: Yup
    .number()
    .positive(validationMessages.positive)
    .max(99999, validationMessages.max(99999))
    .typeError(validationMessages.notValid),
  height: Yup
    .number()
    .positive(validationMessages.positive)
    .max(99999, validationMessages.max(99999))
    .typeError(validationMessages.notValid),
  diameter: Yup
    .number()
    .positive(validationMessages.positive)
    .max(99999, validationMessages.max(99999))
    .typeError(validationMessages.notValid),
  ratingClass: Yup.string(),
  sidemark: Yup
    .string()
    .max(40, "The value must be no more than 40 characters"),
  notes: Yup.string().max(150, "The value must be no more than 150 characters")
}).defined();

export interface PickupRequestModel
  extends PickupShipperModel {
  consignee: PickupConsigneeModel;
  items: ItemModel[];
};

export interface ResponseScrollModel {
  pageSize: number;
  idsMap: {
    date: string;
  };
  lastIds: string;
};

export interface PickupsResponseContentModel {
  shipper: {
    name: string;
    address: AddressWithCoordinates;
  };
  consignee: {
    name: string;
    address: AddressWithCoordinates;
  };
  requester?: {
    teamName?: string;
    customerNumber: number | null;
    teamId: string;
  };
  pickup: {
    date: string;
    readyTime: string;
    closeTime: string;
    pickupNumber: string;
    pickupId: string;
    driverId: number;
    driverName?: string;
    pickupStatus: PickupStatus;
    groupId: string;
    single?: boolean;
    missed?: boolean;
  };
  items: {
    count: number;
    weight: number;
  };
  itemsList: {
    packageType: string;
    units?: number;
    yards?: number;
    weight?: number;
    length?: string;
    width?: string;
    diameter?: string;
    height?: string;
    ratingClass: string;
    description?: string;
    sidemark?: string;
    notes?: string;
    deleted?: boolean; // used to keep index of item in edit mode
    commodityCode?: string;
    commodityCodeDescription?: string;
  }[];
  status: PickupStatus;
  destinationServiceCenter?: {
    terminalNumber: number;
    terminalName: string;
    airportCode: string;
  },
  originServiceCenter: {
    terminalNumber: number;
    terminalName: string;
    airportCode: string;
  },
  pickupDocuments: PickupDocumentModel[];
  billingApprovedDate?: string;
  billingApprovedUser?: string;
  pickupFullData: {
    pickupRequest: {
      probillNumber: number;
      pickupDate: string;
      pickupReadyTime: string;
      pickupCloseTime: string;
      shipperName: string;
      shipperAddress: string;
      shipperCity: string;
      shipperState: string;
      shipperZip: string;
      shipperAddressAdditional: string;
      shipperFirstName: string;
      shipperLastName: string;
      shipperMobilePhone: string;
      shipperAllowSms: boolean;
      shipperEmail: string;
      shipperAccountNumber: number;
      consigneeName: string;
      consigneeAddress1: string;
      consigneeCity: string;
      consigneeState: string;
      consigneePostalCode: string;
      consigneeAddressAdditional: string;
      consigneePhone: string;
      consigneeEmail: string;
      dateCreated: string;
      userCreated: string;
      creatorType: string;
      userUpdated: string;
      dateUpdated: string;
      payorName: string;
      payorAddress: string;
      payorCity: string;
      payorState: string;
      payorZip: string;
      payorPhone: string;
      bolNumber: string;
      poNumber: string;
      additionalReference: string;
      specialInstructions: string;
      documents: PickupDocumentModel[];
      items: ItemModel[];
      manifestNumber: number;
      driverId: number;
    }
  }
};

export interface PickupsResponseModel {
  content: PickupsResponseContentModel[];
  scroll: ResponseScrollModel;
};

export interface PickupsGroupModel {
  id: string;
  shipper: {
    address: string;
  };
  pickup: {
    date: string;
    driverId: number;
  }
  pickups: PickupsResponseContentModel[];
};

export interface PickupUpdateResponseModel {
  id: string;
  pickupStatus: PickupStatus;
  driverId: number;
  driverName?: string;
  groupId: string;
};

export interface StopAddress extends AddressModel, Coordinates { };

export interface AssignedPickup extends Coordinates {
  shipper: {
    address: AddressWithCoordinates,
  },
  pickupStatus: PickupStatus,
  pickupNumber: string,
};

export interface DriverLocation extends Coordinates {
  formattedAddress: string;
};

export interface DriversResponseContentModel {
  driverId: number,
  driverName: string,
  trailerNumber: number,
  tractorNumber: number,
  manifestNumber: number,
  polyline: string,
  stops: {
    order: number,
    type: string,
    status: StopStatus | null,
    address: StopAddress,
  }[],
  pickups: AssignedPickup[],
  driverLocation: DriverLocation;
};

export interface DriversResponseModel {
  content: DriversResponseContentModel[];
  scroll: ResponseScrollModel;
};

export interface PickupAssignRequestModel {
  pickupId: string;
  driverId: number | null;
  status: string;
};

export interface PickupLogItem {
  pickupId: string;
  actionPerformed: string;
  actionSource: string;
  actionExecutedTime: string;
  userExecuted: string;
  driverId: number;
  driverName: string;
  notes: string;
  pickupDate: string;
  pickupNumber: string;
  pickupArrivalLatitude: number;
  pickupArrivalLongitude: number;
  reason?: string | null;
  oldPickupDate?: string | null;
  oldPickupReadyTime?: string | null;
  oldPickupCloseTime?: string | null;
};

export interface PickupRescheduleModel {
  pickupDate: string;
  pickupReadyTime: string | undefined;
  pickupCloseTime: string | undefined;
  reason: string;
  notes?: string;
};

export const PickupRescheduleSchema: Yup.ObjectSchema<PickupRescheduleModel> = Yup.object({
  pickupDate: Yup.string().required(validationMessages.required),
  pickupReadyTime: Yup.string().required(validationMessages.required),
  pickupCloseTime: Yup.string().required(validationMessages.required),
  reason: Yup.string().required(validationMessages.required),
  notes: Yup.string().max(255, "The value must be no more than 255 characters")
}).defined();

export interface PickupCancelModel {
  reason: string;
  notes?: string;
};

export interface ProbillModel {
  probillNumber: number | string | undefined;
};

export const PickupProbillSchema: Yup.ObjectSchema<ProbillModel> = Yup.object({
  probillNumber: Yup.number()
    .notRequired()
    .transform((value, originalValue) => (originalValue === "" ? NaN : Number(originalValue)))
    .min(10000000, validationMessages.notValid)
    .max(999999999, validationMessages.notValid)
    .integer(validationMessages.notValid)
    .typeError(validationMessages.notValid),
}).defined();

export const PickupCancelSchema: Yup.ObjectSchema<PickupCancelModel> = Yup.object({
  reason: Yup.string().required(validationMessages.required),
  notes: Yup.string().max(255, "The value must be no more than 255 characters")
}).defined();

export interface PickupCancelRequestModel extends PickupCancelModel {
  pickupId: string;
};


export interface PickupsLogFilterModel {
  actionPerformed?: string;
  dateFrom?: string;
  dateTo?: string;
  trackingNumber?: string;
  userEmail?: string;
};

export const PickupsLogFilterSchema: Yup.ObjectSchema<PickupsLogFilterModel> = Yup.object({
  actionPerformed: Yup.string(),
  dateFrom: Yup.string(),
  dateTo: Yup.string(),
  trackingNumber: Yup.string().max(50, "The value must be no more than 50 characters"),
  userEmail: Yup.string().max(128, "The value must be no more than 128 characters").email(validationMessages.notValid)
}).defined();

export interface PickupSummaryDto {
  serviceCenter: {
    terminalNumber: number;
    terminalName: string;
    airportCode: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipcode: number;
    terminalRegion: string;
    terminalTimeZone: string;
    managerName: string;
    managerPhoneNumber: string;
    managerEmail: string;
    terminalEmail: string;
    hub: string;
    hubNumb: number | null;
    primary: boolean;
  };
  assignedSummary?: {
    terminalCode: number;
    totalPickups: number;
    totalItems: number;
    totalWeight: number;
    totalDrivers: number;
  };
  unassignedSummary?: {
    terminalCode: number;
    totalPickups: number;
    totalItems: number;
    totalWeight: number;
    totalDrivers: number;
  };
  completedSummary?: {
    terminalCode: number;
    totalPickups: number;
    totalItems: number;
    totalWeight: number;
    totalDrivers: number;
  };
}

export interface PickupDto {
  date: string;
  readyTime: string;
  closeTime: string;
  pickupStatus: PickupStatus;
  pickupNumber: string;
  pickupId: string;
  driverId?: number;
  groupId?: string;
}

export interface PickupDetailsModel {
  pickupId: string;
  pickupNumber: string;
  probillNumber: number;
  pickupStatus: PickupStatus;
  missed?: boolean;
  pickupDate: string;
  pickupReadyTime: string;
  pickupCloseTime: string;
  dateCreated: string;
  userCreated: string;
  creatorType: string;
  userUpdated: string;
  dateUpdated: string;
  bolNumber: string;
  poNumber: string;
  additionalReference: string;
  specialInstructions: string;
  manifestNumber: number;
  driverId?: number;
  driverName?: string;
  requester?: string;
  shipper: PickupShipperDetailsModel;
  consignee: PickupConsigneeModel;
  payor: {
    name: string;
    address: AddressModel;
    phone: string;
  };
  documents?: PickupDocumentModel[];
  items: ItemModel[];
};

export interface UpdatePickupModel {
  pickupId?: string;
  status?: PickupStatus;
  username?: string;
  manifestNumber?: number;
  driverId?: number;
  date?: string;
  readyTime?: string;
  closeTime?: string;
}

export interface PickupsFilterModel {
  pickupSearch: string;
}

export interface TerminalViewPickupSummaryListModel {
  terminalNumber: number;
  terminalName: string;
  created: number;
  completed: number;
  missed: number;
  cancelled: number;
  rescheduled: number;
}

export interface TerminalViewPickupSummaryTilesModel {
  created: number;
  completed: number;
  missed: number;
  rescheduled: number;
  cancelled: number;
}

export interface TerminalViewPickupSummaryFilterListModel {
  currentTerminalCode?: number;
  timePeriod?: string;
  from?: string;
  to?: string;
  region?: string;
  originTerminalNumbers?: XGSSelectOption[];
  destinationTerminalNumbers?: XGSSelectOption[];
}

export interface TerminalViewPickupSummaryDetailModel {
  pickupId: string;
  formattedPickupNumber: string;
  lastEventDate: string;
  requesterCompany: string;
  shipperName: string;
  shipperAddress: string;
  shipperCity: string;
  shipperState: string;
  shipperZip: string;
  consigneeName: string;
  consigneeAddress1: string;
  consigneeAddress2: string | null;
  consigneeCity: string;
  consigneeState: string;
  consigneePostalCode: string;
  totalUnits: number;
  totalWeight: string;
  statusNotes: string | null;
}

//customer view

export interface CustomerViewPickupSummaryListModel {
  requesterCompanyId: string;
  requesterCompany: string;
  created: number;
  completed: number;
  missed: number;
  cancelled: number;
  rescheduled: number;
}

export interface CustomerViewPickupSummaryTilesModel {
  created: number;
  completed: number;
  missed: number;
  rescheduled: number;
  cancelled: number;
}

export interface CustomerViewPickupSummaryFilterListModel {
  currentTerminalCode?: number;
  timePeriod?: string;
  from?: string;
  to?: string;
  requesterCompanyIds?: XGSSelectOption[];
}

export interface CustomerViewPickupSummaryDetailModel {
  pickupId: string;
  formattedPickupNumber: string;
  lastEventDate: string;
  requesterCompany: string;
  shipperName: string;
  shipperAddress: string;
  shipperCity: string;
  shipperState: string;
  shipperZip: string;
  consigneeName: string;
  consigneeAddress1: string;
  consigneeAddress2: string | null;
  consigneeCity: string;
  consigneeState: string;
  consigneePostalCode: string;
  totalUnits: number;
  totalWeight: string;
  statusNotes: string | null;
}

export interface PickupDocumentModel {
  id: string;
  storageUrl: string;
  type: DocumentTypes;
  fileType: string;
}

export enum DocumentTypes {
  BOL = "BOL",
  BOL_PHOTO = "BOL_PHOTO",
  SHL = "SHL",
}

export interface PickupRejectModel {
  note: string;
}

export interface BolUploadRequestModel {
  fileType: string;
  documentData: string;
};

export interface RecognizedBolModel {
  bolNumber?: string,
  poNumber?: string,
  specialInstructions?: string,
  shipFrom: {
    company?: string,
    address: {
      street?: string,
      city?: string,
      state?: string,
      zipcode?: string
    },
    contact: {
      name?: string,
      phoneNumber?: string,
      email?: string
    }
  },
  pickupWindow: {
    date?: string,
    startTime?: string,
    endTime?: string
  },
  shipTo: {
    company?: string,
    address: {
      street?: string,
      city?: string,
      state?: string,
      zipcode?: string
    },
    contact: {
      name?: string,
      phoneNumber?: string,
      email?: string
    },
    receivingHours: {
      startTime?: string,
      endTime?: string
    }
  },
  items: {
    type?: string,
    numberOfHandlingUnits?: number,
    weight?: number,
    pieces?: number,
    freightClass?: string,
    description?: string,
    notes?: string,
    squareYards?: number,
    rollNumber?: string,
    dimensions: {
      length?: number,
      width?: number,
      height?: number
    }
  }[]
};

export const RecognizedBolSchema: Yup.ObjectSchema<RecognizedBolModel> = Yup.object({
  bolNumber: Yup.string().matches(/(\w+|-+)/, validationMessages.notValid).max(20, "The value must be no more than 20 characters"),
  poNumber: Yup.string().matches(/(\w+|-+)/, validationMessages.notValid).max(12, "The value must be no more than 12 characters"),
  specialInstructions: Yup.string().matches(/(\w+|-+)/, validationMessages.notValid).max(150, "The value must be no more than 150 characters"),
  shipFrom: Yup.object().shape({
    company: Yup.string(),
    address: Yup.object().shape({
      street: Yup.string().max(256, "The value must be no more than 255 characters"),
      city: Yup.string().max(64, "The value must be no more than 64 characters"),
      state: Yup.string(),
      zipcode: Yup.string().matches(/^\d*$/, validationMessages.notValid).min(5, "The value must be no less than 5 digits").max(5, "The value must be no more than 5 digits"),
    }).defined(),
    contact: Yup.object().shape({
      name: Yup.string().max(36, "The value must be no more than 36 characters"),
      phoneNumber: Yup.string().max(36, "The value must be no more than 36 characters"),
      email: Yup.string().max(36, "The value must be no more than 36 characters")
    }).defined(),
  }).defined(),
  pickupWindow: Yup.object().shape({
    date: Yup.string(),
    startTime: Yup.string(),
    endTime: Yup.string(),
  }).defined(),
  shipTo: Yup.object().shape({
    company: Yup.string(),
    address: Yup.object().shape({
      street: Yup.string().max(256, "The value must be no more than 255 characters"),
      city: Yup.string().max(64, "The value must be no more than 64 characters"),
      state: Yup.string(),
      zipcode: Yup.string().matches(/^\d*$/, validationMessages.notValid).min(5, "The value must be no less than 5 digits").max(5, "The value must be no more than 5 digits"),
    }).defined(),
    contact: Yup.object().shape({
      name: Yup.string().max(36, "The value must be no more than 36 characters"),
      phoneNumber: Yup.string().max(36, "The value must be no more than 36 characters"),
      email: Yup.string().max(36, "The value must be no more than 36 characters")
    }).defined(),
    receivingHours: Yup.object().shape({
      startTime: Yup.string(),
      endTime: Yup.string()
    }).defined(),
  }).defined(),
  items: Yup.array().of(
    Yup.object().shape({
      type: Yup.string(),
      numberOfHandlingUnits: Yup.number(),
      weight: Yup.number(),
      pieces: Yup.number(),
      freightClass: Yup.string(),
      description: Yup.string(),
      notes: Yup.string(),
      squareYards: Yup.number(),
      rollNumber: Yup.string(),
      dimensions: Yup.object().shape({
        length: Yup.number(),
        width: Yup.number(),
        height: Yup.number()
      }).defined()
    })
  ).defined()
}).defined();

export const PickupRejectSchema: Yup.ObjectSchema<PickupRejectModel> = Yup.object({
  note: Yup.string().required(),
}).defined();

export interface QuickPickupItem {
  packageType: string;
  units: number | null;
  weight: number | null;
  width: number | null;
}

export const QuickPickupItemPackageTypeOptions: XGSSelectOption[] = [
  { label: "Roll", value: "ROLL" },
  { label: "Pallet", value: "PALLET" },
];

export interface QuickPickupConsigneeAddress {
  city: string;
  state: string;
  postalCode: string;
};

export interface QuickPickupCreateModel {
  teamId: string;
  teamName?: string;
  bolNumber?: string;
  poNumber?: string;
  additionalReference?: string;
  specialInstructions: string | undefined;
  pickupDate: string;
  pickupReadyTime: string | undefined;
  pickupCloseTime: string | undefined;
  shipper: {
    id?: string | null;
    accountNumber: number | null;
    name: string;
    address: AddressModel,
    contacts: {
      email: string;
    }
  },
  consigneeLocation: any;
  consignee: {
    address: QuickPickupConsigneeAddress;
  };
  items: QuickPickupItem[];
};

export const QuickPickupItemSchema: Yup.ObjectSchema<QuickPickupItem> = Yup.object({
  packageType: Yup.string().required('Required'),
  units: Yup.number().min(1, 'Units must be at least 1').required('Required'),
  weight: Yup.number().min(1, 'Weight must be greater than 0').required('Required'),
  width: Yup.number().nullable(),
}).defined();

export const QuickPickupCreateSchema: Yup.ObjectSchema<QuickPickupCreateModel> = Yup.object<QuickPickupCreateModel>({
  teamId: Yup.string().required(validationMessages.required),
  bolNumber: Yup.string().matches(/(\w+|-+)/, validationMessages.notValid).max(20, "The value must be no more than 20 characters"),
  poNumber: Yup.string().matches(/(\w+|-+)/, validationMessages.notValid).max(12, "The value must be no more than 12 characters"),
  additionalReference: Yup.string().matches(/(\w+|-+)/, validationMessages.notValid).max(25, "The value must be no more than 25 characters"),
  specialInstructions: Yup.string().matches(/(\w+|-+)/, validationMessages.notValid).max(150, "The value must be no more than 150 characters"),
  pickupDate: Yup.string().required(validationMessages.required),
  pickupReadyTime: Yup.string().required(validationMessages.required),
  pickupCloseTime: Yup.string().required(validationMessages.required),
  shipper: Yup.object().shape({
    accountNumber: Yup.number().nullable().required(validationMessages.required),
    name: Yup.string().required(validationMessages.required).max(36, "The value must be no more than 36 characters"),
    address: Yup.object().shape({
      address1: Yup.string().required(validationMessages.required).max(256, "The value must be no more than 255 characters"),
      city: Yup.string().required(validationMessages.required).max(64, "The value must be no more than 64 characters"),
      postalCode: Yup.string().matches(/^\d*$/, validationMessages.notValid).min(5, "The value must be no less than 5 digits").max(5, "The value must be no more than 5 digits").required(validationMessages.required),
      state: Yup.string().required(validationMessages.required),
      additionalAddressLine: Yup.string().matches(/(\w+|-+)/, validationMessages.notValid).max(150, "The value must be no more than 150 characters"),
    }).defined(),
  }).defined(),
  consigneeLocation: Yup.string().required(validationMessages.required),
  items: Yup.array()
    .of(
      Yup.object({
        packageType: Yup.string().required('Package type is required'),
        units: Yup.number()
          .typeError('Units must be a number')
          .min(1, 'Units must be at least 1')
          .required('Units are required'),
        weight: Yup.number()
          .typeError('Weight must be a number')
          .min(1, 'Weight must be greater than 0')
          .required('Weight is required'),
        width: Yup.string()
          .test('item-width-required', validationMessages.required, function (value) {
            let packageType = this.parent.packageType;
            if (packageType === 'ROLL') {
              return !!value;
            } else {
              return true;
            }
          })
          .nullable()
          .optional(),
      })
    )
    .min(1, 'At least one item is required')
    .max(15, 'Maximum 15 items allowed')
    .required('At least one item is required'),
}).defined();
export interface TerminalViewPickupSummaryListModel {
  terminalNumber: number;
  terminalName: string;
  created: number;
  completed: number;
  missed: number;
  cancelled: number;
  rescheduled: number;
}

export interface TerminalViewPickupSummaryTilesModel {
  created: number,
  completed: number,
  missed: number,
  rescheduled: number,
  cancelled: number
}

export interface TerminalViewPickupSummaryFilterListModel {
  currentTerminalCode?: number;
  timePeriod?: string,
  from?: string;
  to?: string;
  region?: string;
  originTerminalNumbers?: XGSSelectOption[];
  destinationTerminalNumbers?: XGSSelectOption[];
}

export interface TerminalViewDeatilStatusFilterListModel {
  currentTerminalCode?: number;
  timePeriod?: string,
  status?: PickupStatus,
  from?: string;
  to?: string;
  region?: string;
  originTerminalNumbers?: XGSSelectOption[];
  destinationTerminalNumbers?: XGSSelectOption[];
}

export interface TerminalViewPickupSummaryDetailModel {
  pickupId: string,
  formattedPickupNumber: string,
  lastEventDate: string,
  requesterCompany: string,
  shipperName: string,
  shipperAddress: string,
  shipperCity: string,
  shipperState: string,
  shipperZip: string,
  consigneeName: string,
  consigneeAddress1: string,
  consigneeAddress2: string | null,
  consigneeCity: string,
  consigneeState: string,
  consigneePostalCode: string,
  totalUnits: number,
  totalWeight: string,
  statusNotes: string | null
}
